<template>
  <v-dialog
    v-model="showDialog"
    max-width="600px"
    :fullscreen="$vuetify.breakpoint.xsOnly"
    scrollable
    transition="dialog-bottom-transition"
    eager
  >
    <v-card>
      <v-toolbar
        v-if="$vuetify.breakpoint.xsOnly"
        color="primary"
        text
        dark
        class="flex-grow-0"
      >
        <v-btn
          icon
          dark
          @click="showDialog = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>

        <v-toolbar-title>
          {{ editModel.isNew() ? 'New step' : `Edit ${editModel.name}` }}
        </v-toolbar-title>

        <v-spacer />

        <v-toolbar-items v-if="canEdit">
          <v-btn
            v-if="!editModel.isNew()"
            text
            color="error"
            :loading="editModel.saving"
            :disabled="editModel.saving"
            @click="remove()"
          >
            Delete
          </v-btn>

          <v-btn
            dark
            text
            :loading="editModel.saving"
            :disabled="editModel.saving"
            @click="save"
          >
            {{ editModel.isNew() ? 'Create' : 'Save' }}
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>

      <v-card-title v-else>
        {{ editModel.isNew() ? 'New step' : `Edit ${editModel.name}` }}
      </v-card-title>

      <v-divider v-if="$vuetify.breakpoint.smAndUp" />

      <v-card-text :style="$vuetify.breakpoint.smAndUp ? 'height: 500px' : ''">
        <v-form
          ref="form"
          v-model="valid"
        >
          <v-container grid-list-xl>
            <v-layout wrap>
              <v-flex
                xs12
                md6
              >
                <v-text-field
                  v-model="editModel.name"
                  label="Name"
                  :error-messages="editModel.errors.name"
                />
              </v-flex>

              <v-flex
                xs12
                md6
              >
                <v-text-field
                  ref="mapAutocomplete"
                  :value="editModel.location"
                  label="Location"
                  prepend-icon="mdi-map-marker"
                />
              </v-flex>

              <v-flex
                xs12
                md6
              >
                <v-menu
                  v-model="startDateMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      :value="fmtDate(editModel.startDate)"
                      label="Start Date"
                      readonly
                      prepend-icon="mdi-calendar"
                      :error-messages="editModel.errors.startDate"
                      clearable
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    v-model="editModel.startDate"
                    @change="startDateMenu = false"
                  />
                </v-menu>
              </v-flex>

              <v-flex
                xs12
                md6
              >
                <v-menu
                  v-model="endDateMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      :value="fmtDate(editModel.endDate)"
                      label="End Date"
                      readonly
                      prepend-icon="mdi-calendar"
                      clearable
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    v-model="editModel.endDate"
                    :allowed-dates="allowedEndDate"
                    @change="endDateMenu = false"
                  />
                </v-menu>
              </v-flex>

              <v-flex
                xs12
                md6
              >
                <v-text-field
                  v-model.number="editModel.cost"
                  :error-messages="editModel.errors.cost"
                  label="Cost"
                  prefix="€"
                  placeholder="0.00"
                />
              </v-flex>

              <v-flex
                xs12
                md6
              >
                <v-text-field
                  v-model.number="editModel.url"
                  :error-messages="editModel.errors.url"
                  label="Web page"
                  prepend-icon="mdi-link"
                  placeholder="www..."
                />
              </v-flex>

              <vu-file-upload
                v-if="false"
                accept="*/*"
                type="file"
                :firestore-ref="stepRef"
                firestore-field="attachment"
                :storage-path="editModel.tripId"
              />

              <v-flex
                xs12
              >
                <span class="subheading">Type</span>

                <v-chip-group
                  v-model="editModel.type"
                  column
                  active-class="accent--text"
                >
                  <v-chip
                    v-for="type in allStepTypes"
                    :key="type.value"
                    :value="type.value"
                  >
                    <v-icon left>
                      {{ type.icon }}
                    </v-icon>
                    {{ type.text }}
                  </v-chip>
                </v-chip-group>
              </v-flex>
            </v-layout>
          </v-container>
        </v-form>
      </v-card-text>

      <v-divider v-if="$vuetify.breakpoint.smAndUp" />

      <v-card-actions
        v-if="$vuetify.breakpoint.smAndUp && canEdit"
      >
        <v-btn
          v-if="!editModel.isNew()"
          text
          color="error"
          :loading="editModel.saving"
          :disabled="editModel.saving"
          @click="remove()"
        >
          Delete
        </v-btn>

        <v-spacer />

        <v-btn
          text
          color="primary"
          :loading="editModel.saving"
          :disabled="editModel.saving"
          @click="save"
        >
          {{ editModel.isNew() ? 'Create' : 'Save' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
/// <reference types="@types/googlemaps" />

import { allStepTypes } from './TripStepTypes'
import VuFileUpload from '@/components/VuFileUpload'
import { fmtDate } from '@/plugins/filters'
import { analytics, firestore } from '@/plugins/firebase'

const PLACES_TYPES_PLACE = [
  'accounting',
  'airport',
  'amusement_park',
  'aquarium',
  'art_gallery',
  'atm',
  'bakery',
  'bank',
  'bar',
  'beauty_salon',
  'bicycle_store',
  'book_store',
  'bowling_alley',
  'bus_station',
  'cafe',
  'campground',
  'car_dealer',
  'car_rental',
  'car_repair',
  'car_wash',
  'casino',
  'cemetery',
  'church',
  'city_hall',
  'clothing_store',
  'convenience_store',
  'courthouse',
  'dentist',
  'department_store',
  'doctor',
  'drugstore',
  'electrician',
  'electronics_store',
  'embassy',
  'fire_station',
  'florist',
  'funeral_home',
  'furniture_store',
  'gas_station',
  'grocery_or_supermarket',
  'gym',
  'hair_care',
  'hardware_store',
  'hindu_temple',
  'home_goods_store',
  'hospital',
  'insurance_agency',
  'jewelry_store',
  'laundry',
  'lawyer',
  'library',
  'light_rail_station',
  'liquor_store',
  'local_government_office',
  'locksmith',
  'lodging',
  'meal_delivery',
  'meal_takeaway',
  'mosque',
  'movie_rental',
  'movie_theater',
  'moving_company',
  'museum',
  'night_club',
  'painter',
  'park',
  'parking',
  'pet_store',
  'pharmacy',
  'physiotherapist',
  'plumber',
  'police',
  'post_office',
  'primary_school',
  'real_estate_agency',
  'restaurant',
  'roofing_contractor',
  'rv_park',
  'school',
  'secondary_school',
  'shoe_store',
  'shopping_mall',
  'spa',
  'stadium',
  'storage',
  'store',
  'subway_station',
  'supermarket',
  'synagogue',
  'taxi_stand',
  'tourist_attraction',
  'train_station',
  'transit_station',
  'travel_agency',
  'university',
  'veterinary_care',
  'zoo'
]

const PLACES_TYPES_GEO = [
  'administrative_area_level_1',
  'administrative_area_level_2',
  'administrative_area_level_3',
  'administrative_area_level_4',
  'administrative_area_level_5',
  'archipelago',
  'colloquial_area',
  'continent',
  'country',
  'establishment',
  'finance',
  'floor',
  'food',
  'general_contractor',
  'geocode',
  'health',
  'intersection',
  'locality',
  'natural_feature',
  'neighborhood',
  'place_of_worship',
  'point_of_interest',
  'political',
  'post_box',
  'postal_code',
  'postal_code_prefix',
  'postal_code_suffix',
  'postal_town',
  'premise',
  'room',
  'route',
  'street_address',
  'street_number',
  'sublocality',
  'sublocality_level_1',
  'sublocality_level_2',
  'sublocality_level_3',
  'sublocality_level_4',
  'sublocality_level_5',
  'subpremise',
  'town_square'
]

export default {
  components: {
    VuFileUpload
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    editModel: {
      type: Object,
      required: true
    },
    trip: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      allStepTypes,
      endDateMenu: false,
      place: null,
      startDateMenu: false,
      valid: true
    }
  },
  computed: {
    canEdit () {
      if (this.$currentUser) {
        const role = this.trip.users[this.$currentUser.id]
        return role === 'owner' || role === 'editor'
      } else {
        return false
      }
    },
    showDialog: {
      get () { return this.show },
      set (val) {
        if (!val) {
          this.editModel.reset()
        } else {
          analytics.logEvent('screen_view', { screen_name: 'trip_step_create' })
        }
        this.$emit('update:show', val)
      }
    },
    stepRef () {
      return this.editModel.id ? firestore.collection('trip-steps').doc(this.editModel.id) : null
    }
  },
  mounted () {
    /* global google */
    // eslint-disable-next-line no-new
    const autocomplete = new google.maps.places.Autocomplete(
      this.$refs.mapAutocomplete.$el.querySelector('input'),
      {
        // types: [ 'geocode' ]
      })

    // return only basic data, included in Places billed request
    autocomplete.setFields([
      'address_components',
      'geometry',
      'adr_address',
      'formatted_address',
      'geometry',
      'icon',
      'name',
      'permanently_closed',
      // 'photo', // photos are paid per view!
      'type',
      'url',
      'vicinity'
    ])

    // workaround to remove Automplete placeholder
    this.$refs.mapAutocomplete.$el.querySelector('input').setAttribute('placeholder', '')

    google.maps.event.addListener(autocomplete, 'place_changed', () => {
      const place = autocomplete.getPlace()
      this.place = place // for vue debugging

      for (const comp of place.address_components) {
        if (comp.types.includes('country')) {
          this.editModel.country = comp.short_name
        }
      }

      // if it's a commercial place (not a street, city or geo location),
      // set name and url of the step
      if (this.isPlace(place)) {
        this.editModel.name = place.name
        this.editModel.url = place.url
      }
      this.editModel.type = this.guessStepType(place)
      this.editModel.location = place.formatted_address
      this.$refs.mapAutocomplete.$el.querySelector('input').value = place.formatted_address
      this.editModel.lat = place.geometry.location.lat()
      this.editModel.lng = place.geometry.location.lng()
    })
  },
  methods: {
    fmtDate,
    async save () {
      const isNew = this.editModel.isNew()

      await this.editModel.save()

      if (isNew) { analytics.logEvent('trip_step_create') }
      this.showDialog = false
      this.$emit('save')
    },
    remove () {
      this.editModel
        .delete()
        .then(() => {
          this.showDialog = false
        })
    },
    allowedEndDate (val) {
      if (this.editModel.startDate) {
        return val > this.editModel.startDate
      } else {
        return true
      }
    },

    /**
     * Check if the Place is a street, country or any other geographical location
     * rather than a resturant, bar, cinema or any other place
     *
     * @param {google.maps.places.PlaceResult} place a Google PlaceResult object
     * @return {boolean}
     */
    isGeoLocation (place) {
      return place.types.some(item => PLACES_TYPES_GEO.includes(item))
    },

    /**
     * Check if the Place is a resturant, bar, cinema or any other place
     * rather than a street, country or any other geographical location
     *
     * @param {google.maps.places.PlaceResult} place a Google PlaceResult object
     * @return {boolean}
     */
    isPlace (place) {
      return place.types.some(item => PLACES_TYPES_PLACE.includes(item))
    },

    /**
     * Infer TripStep type from the selected Google Place
     *
     * @param {google.maps.places.PlaceResult} place a Google PlaceResult object
     * @return {string}
     */
    guessStepType (place) {
      for (let type of place.types) {
        switch (type) {
          case 'airport':
            return 'flight'

          case 'light_rail_station':
          case 'subway_station':
          case 'train_station':
          case 'transit_station':
            return 'train'

          case 'bus_station':
            return 'bus'

          case 'car_rental':
          case 'gas_station':
          case 'parking':
          case 'rv_park':
          case 'taxi_stand':
            return 'car'

          case 'campground':
          case 'lodging':
            return 'accommodation'

          case 'bar':
          case 'cafe':
            return 'drink'

          case 'bakery':
          case 'grocery_or_supermarket':
          case 'meal_delivery':
          case 'meal_takeaway':
          case 'restaurant':
          case 'supermarket':
            return 'food'

          case 'bicycle_store':
          case 'book_store':
          case 'clothing_store':
          case 'convenience_store':
          case 'department_store':
          case 'drugstore':
          case 'electronics_store':
          case 'florist':
          case 'furniture_store':
          case 'hardware_store':
          case 'home_goods_store':
          case 'jewelry_store':
          case 'library':
          case 'liquor_store':
          case 'pet_store':
          case 'shoe_store':
          case 'shopping_mall':
          case 'store':
            return 'shopping'

          case 'amusement_park':
          case 'bowling_alley':
          case 'casino':
          case 'movie_theater':
          case 'night_club':
          case 'stadium':
          case 'art_gallery':
          case 'aquarium':
          case 'museum':
          case 'tourist_attraction':
          case 'zoo':
          case 'church':
          case 'hindu_temple':
          case 'mosque':
          case 'park':
          case 'synagogue':
            return 'attraction'

          case 'beauty_salon':
          case 'gym':
          case 'hair_care':
          case 'spa':
          case 'dentist':
          case 'doctor':
          case 'hospital':
          case 'pharmacy':
          case 'physiotherapist':
          case 'veterinary_care':
            return 'wellness'

          case 'accounting':
          case 'atm':
          case 'bank':
          case 'car_dealer':
          case 'car_repair':
          case 'car_wash':
          case 'cemetery':
          case 'electrician':
          case 'funeral_home':
          case 'insurance_agency':
          case 'laundry':
          case 'lawyer':
          case 'locksmith':
          case 'moving_company':
          case 'movie_rental':
          case 'painter':
          case 'plumber':
          case 'post_office':
          case 'real_estate_agency':
          case 'roofing_contractor':
          case 'storage':
          case 'travel_agency':
            return 'service'

          case 'city_hall':
          case 'courthouse':
          case 'embassy':
          case 'fire_station':
          case 'local_government_office':
          case 'police':
          case 'primary_school':
          case 'school':
          case 'secondary_school':
          case 'university':
            return 'institution'
        }
      }
    }
  }
}
</script>

<style>

</style>
